import React, { FC, ReactNode, useContext, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import AuthContext from '../../contexts/authContext';
import { UserType } from '../../common/data/dummyRolesData';
import Loading from '../Loading';

interface IRoleProtectedRouteProps {
	children: ReactNode;
	allowedRoles?: UserType[];
	unauthorizedComponent?: ReactNode;
}

const RoleProtectedRoute: FC<IRoleProtectedRouteProps> = ({
	children,
	allowedRoles,
	unauthorizedComponent,
}) => {
	const { isAuthenticated, isLoading, userData } = useContext(AuthContext);
	const location = useLocation();
	const userRole = userData?.role;
	const path = location.pathname;

	// Show loading indicator while authentication state is being determined
	if (isLoading) {
		return <Loading />;
	}

	// Redirect to login page if not authenticated
	if (!isAuthenticated) {
		return <Navigate to='/' replace />;
	}

	// Role-based path protection (always checked first)
	if (userRole) {
		// Company users can only access company routes
		if (userRole === UserType.COMPANY) {
			// If company user tries to access freelance routes
			if (path.startsWith('/talent')) {
				// Display unauthorized component if provided, otherwise redirect
				return unauthorizedComponent ? (
					<>{unauthorizedComponent}</>
				) : (
					<Navigate to='/company/dashboard' replace />
				);
			}
		}

		// Talent users can only access freelance routes
		if (userRole === UserType.FREELANCE) {
			// If freelance user tries to access company routes
			if (path.startsWith('/company')) {
				// Display unauthorized component if provided, otherwise redirect
				return unauthorizedComponent ? (
					<>{unauthorizedComponent}</>
				) : (
					<Navigate to='/talent/dashboard' replace />
				);
			}
		}

		// Check explicit allowedRoles for specific route components
		if (allowedRoles && !allowedRoles.some((role) => role === userRole)) {
			// Display unauthorized component if provided, otherwise redirect based on role
			if (unauthorizedComponent) {
				return <>{unauthorizedComponent}</>;
			}

			// Fall back to role-based redirect
			if (userRole === UserType.COMPANY) {
				return <Navigate to='/company/dashboard' replace />;
			} else if (userRole === UserType.FREELANCE) {
				return <Navigate to='/talent/dashboard' replace />;
			}
		}
	} else {
		// If userRole is undefined or null, log for debugging
		console.error('No user role found in userData:', userData);
	}

	return <>{children}</>;
};

export default RoleProtectedRoute;
